/**
 * Thin wrapper around Matomo's `_paq` push-queue API. Pushing to the array is
 * safe before the tracker script has loaded — matomo.js drains the queue once
 * it initializes, so call order here only matters relative to itself.
 */
import { getTrustedScriptURL } from './trustedTypes';

declare global {
  interface Window {
    _paq?: unknown[][];
  }
}

const MATOMO_URL = 'https://a.caffeina-ba.com/';
const SITE_ID = '1';

function paq(): unknown[][] {
  window._paq ??= [];
  return window._paq;
}

/** Loads the tracker script once. Safe to call more than once. */
export function initAnalytics(): void {
  if (document.getElementById('matomo-tracker')) return;

  paq().push(['enableLinkTracking']);
  /**
   * Uncaught JavaScript errors become Matomo events. Without this, a crash on
   * a customer's phone is invisible: they see a broken page, close it, and no
   * trace of it ever reaches us. Matomo records the message, file and line.
   */
  paq().push(['enableJSErrorTracking']);
  paq().push(['setTrackerUrl', `${MATOMO_URL}matomo.php`]);
  paq().push(['setSiteId', SITE_ID]);

  const script = document.createElement('script');
  script.id = 'matomo-tracker';
  script.async = true;
  // Goes through the Trusted Types policy so this keeps working once the CSP
  // requires one — see trustedTypes.ts.
  script.src = getTrustedScriptURL(`${MATOMO_URL}matomo.js`) as string;
  document.head.appendChild(script);
}

/** Fired on every route change, including the first render. */
export function trackPageView(path: string): void {
  paq().push(['setCustomUrl', window.location.origin + path]);
  paq().push(['setDocumentTitle', document.title]);
  paq().push(['trackPageView']);
}

export function trackEvent(category: string, action: string, name?: string): void {
  paq().push(['trackEvent', category, action, name]);
}

/**
 * Reports an error we caught ourselves, which `enableJSErrorTracking` cannot
 * see — React swallows render errors into the nearest boundary rather than
 * letting them reach window.onerror.
 *
 * `where` identifies the screen; the message carries the detail. Nothing about
 * the customer is attached beyond what the tracker already knows.
 */
export function trackError(where: string, error: unknown): void {
  const message =
    error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  // Matomo truncates long event names; a stack would be cut off anyway, and
  // the message plus screen is what makes it findable.
  paq().push(['trackEvent', 'Error', where, message.slice(0, 250)]);
}

/**
 * Ties subsequent tracking to a signed-in customer's internal id (not their
 * email), so behaviour can be followed across devices without storing PII in
 * the analytics database.
 */
export function setAnalyticsUserId(userId: string): void {
  paq().push(['setUserId', userId]);
}

export function clearAnalyticsUserId(): void {
  paq().push(['resetUserId']);
}
